// src/pages/email-template-editor.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getEmailTemplate, saveEmailTemplate, deleteEmailTemplate, previewEmailTemplate } from '../services/api.service';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { FaChevronLeft } from 'react-icons/fa';

const langs = ['fr', 'en', 'de'];
const flags = { fr: '/assets/images/fr.png', en: '/assets/images/en.png', de: '/assets/images/de.png' };
const normalizeI18n = (o) => langs.reduce((acc, l) => ({ ...acc, [l]: o?.[l] || '' }), { fr: '', en: '', de: '' });

const TRIGGER_OPTIONS = [
    { value: 'any', label: 'Toujours' },
    { value: 'status.created', label: 'À la création' },
    { value: 'status.paid', label: 'Quand payé' },
    { value: 'status.in-progress', label: 'En cours' },
    { value: 'status.completed', label: 'Quand complété' },
    { value: 'status.rejected', label: 'Quand rejeté' },
];

const defaultTpl = {
    enable: true,
    name: { fr: '', en: '', de: '' },
    subject: { fr: '', en: '', de: '' },
    body: { fr: '', en: '', de: '' }, // can store full HBS/HTML here
    trigger: 'any',
    toType: 'customer',
    toCustomEmails: [],
    cc: [],
    bcc: [],
    languageStrategy: 'auto',
    fixedLang: 'fr',
};

const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};
const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const DEFAULT_CONTEXT = `{
  "userFirstName": "Jean",
  "customer": { "firstName": "Jean", "email": "jean.dupont@example.com" },
  "order": { "number": "FA-2025-001", "total": "99,90 €" },
  "service": { "title": "Inscription VDI" },
  "now": "${new Date().toISOString()}"
}`;

const EmailTemplateEditor = () => {
    const { id } = useParams();
    const isEdit = Boolean(id && id !== 'new');
    const navigate = useNavigate();
    const confirm = useConfirm();

    const [lang, setLang] = useState('fr');
    const [tpl, setTpl] = useState(null);

    // editor mode: 'visual' uses Quill; 'hbs' uses textarea (for full .hbs/.html)
    const [editorMode, setEditorMode] = useState('hbs'); // default to HBS for full email layouts

    // preview
    const [contextJson, setContextJson] = useState(DEFAULT_CONTEXT);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        if (isEdit) {
            getEmailTemplate(id)
                .then((d) =>
                    setTpl({
                        ...d,
                        name: normalizeI18n(d.name),
                        subject: normalizeI18n(d.subject),
                        body: normalizeI18n(d.body),
                        trigger: d.trigger || 'any',
                        toCustomEmails: d.toCustomEmails || [],
                        cc: d.cc || [],
                        bcc: d.bcc || [],
                    })
                )
                .catch(() => {
                    Helper.toast("Impossible de charger le modèle d'email", 'error');
                    navigate('/email-templates');
                });
        } else {
            setTpl({ ...defaultTpl });
        }
    }, [id, isEdit, navigate]);

    const updateField = (k, v) => setTpl((s) => ({ ...s, [k]: v }));
    const updateNested = (section, l, v) => setTpl((s) => ({ ...s, [section]: { ...s[section], [l]: v } }));

    const canSave = useMemo(() => {
        const subjectOk = (tpl?.subject?.fr || tpl?.subject?.en || tpl?.subject?.de || '').trim().length > 0;
        const nameOk = (tpl?.name?.fr || tpl?.name?.en || tpl?.name?.de || '').trim().length > 0;
        const customOk = tpl?.toType !== 'custom' || (Array.isArray(tpl?.toCustomEmails) && tpl.toCustomEmails.length > 0);
        return Boolean(nameOk && subjectOk && customOk);
    }, [tpl]);

    const doPreview = async () => {
        let context;
        try {
            context = JSON.parse(contextJson || '{}');
        } catch (e) {
            return Helper.toast('JSON de contexte invalide', 'error');
        }
        try {
            const out = await previewEmailTemplate({
                subject: tpl.subject, // i18n object
                body: tpl.body,       // i18n object (may contain full .hbs)
                lang,
                context,
            });
            setPreviewSubject(out.subject || '');
            setPreviewHtml(out.html || '');
            setPreviewOpen(true);
        } catch {
            // toast handled in api if needed
        }
    };

    if (!tpl) return <div className="p-6 text-center text-gray-500">Chargement…</div>;

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Sticky header */}
            <div className="sticky top-0 z-10 mb-6 bg-white">
                <div className="flex justify-between items-center py-3">
                    <button
                        onClick={() => navigate('/email-templates')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <FaChevronLeft className="h-4 w-4" /> Retour
                    </button>

                    <div className="flex items-center gap-3">
                        {/* Lang switch (desktop) */}
                        <div className="hidden sm:flex items-center gap-1 border rounded-xl bg-white p-1">
                            {langs.map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setLang(l)}
                                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    title={l.toUpperCase()}
                                >
                                    <img src={flags[l]} alt={l} className="w-5 h-4 rounded-sm" />
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        {/* Editor mode toggle */}
                        <div className="inline-flex rounded-lg border bg-white overflow-hidden">
                            {[
                                { v: 'visual', label: 'Éditeur visuel' },
                                { v: 'hbs', label: 'HTML / HBS' },
                            ].map((opt) => (
                                <button
                                    key={opt.v}
                                    onClick={() => setEditorMode(opt.v)}
                                    className={`px-3 py-2 text-sm ${editorMode === opt.v ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {isEdit && (
                            <button
                                onClick={async () => {
                                    const ok = await confirm({
                                        title: 'Supprimer ?',
                                        message: <>Supprimer ce modèle <strong>{tpl.name?.fr || '(Sans nom)'}</strong> ?</>,
                                        confirmText: 'Oui',
                                        cancelText: 'Non',
                                        tone: 'warning',
                                    });
                                    if (!ok) return;
                                    try {
                                        await deleteEmailTemplate(tpl._id);
                                        navigate('/email-templates');
                                    } catch { }
                                }}
                                className="px-4 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50"
                            >
                                Supprimer
                            </button>
                        )}

                        <button
                            onClick={doPreview}
                            className="px-4 py-2 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                        >
                            Aperçu
                        </button>

                        <button
                            onClick={async () => {
                                try {
                                    await saveEmailTemplate(tpl);
                                    navigate('/email-templates');
                                } catch { }
                            }}
                            disabled={!canSave}
                            className={`px-4 py-2 rounded text-white shadow ${canSave ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'
                                }`}
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>

            {/* Status + mobile lang */}
            <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-6 mb-6">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">Statut</span>
                        <div className="inline-flex rounded-lg border bg-white overflow-hidden">
                            {[{ v: true, label: 'Activé' }, { v: false, label: 'Désactivé' }].map((opt) => (
                                <button
                                    key={String(opt.v)}
                                    onClick={() => updateField('enable', opt.v)}
                                    className={`px-3 py-2 text-sm ${tpl.enable === opt.v ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="sm:hidden">
                        <select value={lang} onChange={(e) => setLang(e.target.value)} className="border rounded px-2 py-1">
                            {langs.map((l) => (
                                <option key={l} value={l}>{l.toUpperCase()}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Name */}
                <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Nom interne ({lang.toUpperCase()})</label>
                    <input
                        type="text"
                        value={tpl.name[lang]}
                        onChange={(e) => updateNested('name', lang, e.target.value)}
                        className="w-full border rounded px-3 py-2 shadow-sm"
                        placeholder="Ex: Confirmation de commande"
                    />
                </div>

                <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Déclencheur</label>
                    <select
                        value={tpl.trigger || 'any'}
                        onChange={(e) => updateField('trigger', e.target.value)}
                        className="w-full border rounded px-3 py-2 shadow-sm"
                    >
                        {TRIGGER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Sélectionnez quand ce modèle doit être envoyé. Utilisez <strong>Toujours</strong> pour un modèle générique.
                    </p>
                </div>

                {/* Subject */}
                <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Sujet ({lang.toUpperCase()})</label>
                    <input
                        type="text"
                        value={tpl.subject[lang]}
                        onChange={(e) => updateNested('subject', lang, e.target.value)}
                        className="w-full border rounded px-3 py-2 shadow-sm"
                        placeholder="Votre commande {{order.number}} est confirmée"
                    />
                    <p className="text-xs text-gray-500 mt-1">Le sujet accepte aussi les variables Handlebars.</p>
                </div>

                {/* Body: Visual or HBS */}
                <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                        Contenu ({editorMode === 'visual' ? 'Éditeur visuel' : 'HTML / HBS'}) — {lang.toUpperCase()}
                    </label>

                    {editorMode === 'visual' ? (
                        <ReactQuill
                            key={`body-visual-${lang}`}
                            theme="snow"
                            value={tpl.body[lang]}
                            onChange={(v) => updateNested('body', lang, v)}
                            className="bg-white rounded shadow"
                            modules={quillModules}
                            formats={quillFormats}
                        />
                    ) : (
                        <textarea
                            key={`body-hbs-${lang}`}
                            className="w-full h-[380px] border rounded px-3 py-2 font-mono text-sm"
                            value={tpl.body[lang]}
                            onChange={(e) => updateNested('body', lang, e.target.value)}
                            placeholder="Collez ici votre template HTML / HBS complet (DOCTYPE, head, style, body...)"
                        />
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                        Pour des emails complets (doctype, head, CSS inline), utilisez <strong>HTML / HBS</strong>. L’éditeur visuel
                        est plutôt pour des contenus simples.
                    </p>
                </div>
            </div>

            {/* Recipients & Language */}
            <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-5">
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Destinataires</label>
                        <select
                            value={tpl.toType}
                            onChange={(e) => updateField('toType', e.target.value)}
                            className="w-full border rounded px-3 py-2"
                        >
                            <option value="customer">Client</option>
                            <option value="admin">Admin</option>
                            <option value="custom">Emails personnalisés</option>
                        </select>

                        {tpl.toType === 'custom' && (
                            <div className="mt-2">
                                <input
                                    type="text"
                                    placeholder="email1@exemple.com, email2@exemple.com"
                                    className="w-full border rounded px-3 py-2"
                                    value={(tpl.toCustomEmails || []).join(', ')}
                                    onChange={(e) =>
                                        updateField('toCustomEmails', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))
                                    }
                                />
                                <p className="text-xs text-gray-500 mt-1">Séparés par des virgules.</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <input
                                type="text"
                                placeholder="CC (optionnel)"
                                className="w-full border rounded px-3 py-2"
                                value={(tpl.cc || []).join(', ')}
                                onChange={(e) => updateField('cc', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
                            />
                            <input
                                type="text"
                                placeholder="BCC (optionnel)"
                                className="w-full border rounded px-3 py-2"
                                value={(tpl.bcc || []).join(', ')}
                                onChange={(e) => updateField('bcc', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
                            />
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-5">
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Langue d’envoi</label>
                        <div className="flex items-center gap-2">
                            <select
                                value={tpl.languageStrategy}
                                onChange={(e) => updateField('languageStrategy', e.target.value)}
                                className="border rounded px-3 py-2"
                            >
                                <option value="auto">Auto (selon le client)</option>
                                <option value="fixed">Fixe</option>
                            </select>
                            {tpl.languageStrategy === 'fixed' && (
                                <select
                                    value={tpl.fixedLang || 'fr'}
                                    onChange={(e) => updateField('fixedLang', e.target.value)}
                                    className="border rounded px-3 py-2"
                                >
                                    {langs.map((l) => (
                                        <option key={l} value={l}>{l.toUpperCase()}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                    {/* test context + preview */}
                    <div className="pt-2 border-t">
                        <label className="block mb-1 text-sm font-medium text-gray-700">Données de test (JSON)</label>
                        <textarea
                            className="w-full h-36 border rounded px-3 py-2 font-mono text-xs"
                            value={contextJson}
                            onChange={(e) => setContextJson(e.target.value)}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Utilisé pour l’aperçu. Exemples de variables : <code>{'{{userFirstName}}'}</code>,{' '}
                            <code>{'{{customer.firstName}}'}</code>, <code>{'{{order.number}}'}</code>, <code>{'{{service.title}}'}</code>, <code>{'{{comment}}'}</code>.
                        </p>
                    </div>
                </div>
            </div>

            {/* Preview panel */}
            {previewOpen && (
                <div className="mt-6 rounded-2xl border bg-white shadow-sm">
                    <div className="flex items-center justify-between px-5 py-3 border-b">
                        <div className="text-sm text-gray-700 truncate"><strong>Sujet rendu:</strong> {previewSubject}</div>
                        <button className="text-gray-600 hover:text-gray-900" onClick={() => setPreviewOpen(false)}>Fermer</button>
                    </div>
                    <div className="p-0">
                        <iframe
                            title="email-preview"
                            className="w-full h-[700px]"
                            sandbox=""              // no scripts allowed (safe preview)
                            srcDoc={previewHtml}    // renders your full DOCTYPE+HEAD+BODY email
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTemplateEditor;